import { useTranslation } from 'react-i18next';

import type { ComponentType } from 'react';

import { IconButton, MenuItem, Stack, styled, Tooltip } from '@mui/material';

import { Colors } from 'Themes/colors';

import { DeleteIcon, IconSizes, OpenInNewIcon, RemoveCircleIcon } from '@libs/shared/ui/icons';

import { SettingsMenu } from './settings-menu';

import type { DesktopModel } from 'Api';

const StyledStack = styled(Stack)<{ settingsOpened: boolean }>`
  display: flex;
  flex-direction: row !important;
  flex-wrap: nowrap;

  ${({ settingsOpened }) =>
    settingsOpened &&
    `
      & > *:last-child {
        margin-left: 16px;
      }
    `}
`;

const StyledMenuItem = styled(MenuItem)`
  display: flex;
  gap: 10px;
  > svg {
    color: ${Colors.green};
  }
`;

type Props = {
  desktop: DesktopModel;
  isFullscreenEnabled: boolean;
  isTrainerDesktopPreview: boolean;
  canControlMachine: boolean;
  settingsOpened: boolean;
  toggleSettings: () => void;
  handleOpenPreviewOnNewTab: () => void;
  handleStopMachine: () => void;
  displayDialog: () => void;
  FullscreenIcon: ComponentType;
  toggleFullscreenMode: () => void;
};

export const ActionButtons = ({
  desktop,
  isFullscreenEnabled,
  isTrainerDesktopPreview,
  canControlMachine,
  settingsOpened,
  toggleSettings,
  handleOpenPreviewOnNewTab,
  handleStopMachine,
  displayDialog,
  FullscreenIcon,
  toggleFullscreenMode
}: Readonly<Props>) => {
  const { t } = useTranslation();

  return (
    <StyledStack settingsOpened={settingsOpened}>
      <Tooltip title={t('FullScreen')}>
        <IconButton size="small" color="primary" onClick={toggleFullscreenMode}>
          <FullscreenIcon />
        </IconButton>
      </Tooltip>

      {!isFullscreenEnabled ? (
        <Tooltip title={t('OpenOnNewTab')}>
          <IconButton
            size="small"
            onClick={handleOpenPreviewOnNewTab}
            disabled={!desktop?.instanceId}
            sx={{ color: Colors.darkGrey }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      ) : null}

      {!isTrainerDesktopPreview && !isFullscreenEnabled && canControlMachine ? (
        <SettingsMenu disabled={!desktop?.instanceId} open={settingsOpened} toggle={toggleSettings}>
          {desktop?.isRunning && (
            <StyledMenuItem key={0} onClick={handleStopMachine}>
              <RemoveCircleIcon />
              {t('StopMachine')}
            </StyledMenuItem>
          )}

          {desktop?.instanceId && (
            <StyledMenuItem key={1} onClick={displayDialog}>
              <DeleteIcon fontSize={IconSizes.small} />
              {t('RemoveMachine', { instanceName: desktop?.instanceName })}
            </StyledMenuItem>
          )}
        </SettingsMenu>
      ) : null}
    </StyledStack>
  );
};
